import React, { useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  Analytics as AnalyticsIcon,
  ArrowForward as ArrowForwardIcon,
  Badge as BadgeIcon,
  BarChart as BarChartIcon,
  CheckCircle as CheckCircleIcon,
  ContentPasteSearch as ContentPasteSearchIcon,
  CorporateFare as CorporateFareIcon,
  Dashboard as DashboardIcon,
  FactCheck as FactCheckIcon,
  Forest as ForestIcon,
  GridView as GridViewIcon,
  Groups as GroupsIcon,
  Menu as MenuIcon,
  PhoneIphone as PhoneIphoneIcon,
  QrCode2 as QrCode2Icon,
  Savings as SavingsIcon,
  Shield as ShieldIcon,
  Speed as SpeedIcon,
  Verified as VerifiedIcon,
  Visibility as VisibilityIcon,
  VpnKey as VpnKeyIcon,
  Workspaces as WorkspacesIcon,
} from "@mui/icons-material";

const DEMO_EMAIL =
  "mailto:businesscardsbotswana@gmail.com?subject=Request%20Demo%20-%20BusinessCards%20BW";

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "ESG", href: "#esg" },
  { label: "Verification", href: "#verification" },
  { label: "Analytics", href: "#analytics" },
  { label: "Pricing", href: "#pricing" },
];

const TRUST_BADGES = [
  "Verified employee profiles",
  "ESG-aligned networking",
  "Admin-controlled branding",
  "QR & vCard ready",
];

const PROBLEM_ITEMS = [
  {
    title: "Printing and reprinting costs",
    body: "Every title change, phone update or team expansion creates another print cycle and another procurement cost.",
    icon: <SavingsIcon sx={{ fontSize: 28, color: "#7CB5FF" }} />,
  },
  {
    title: "Outdated titles and contact details",
    body: "Printed cards keep circulating long after details change, undermining trust and slowing follow-up.",
    icon: <ContentPasteSearchIcon sx={{ fontSize: 28, color: "#7CB5FF" }} />,
  },
  {
    title: "Ex-employees retaining company cards",
    body: "Offboarded staff can still appear legitimate when physical cards remain in the market.",
    icon: <ShieldIcon sx={{ fontSize: 28, color: "#7CB5FF" }} />,
  },
  {
    title: "No analytics or visibility after cards are shared",
    body: "Printed cards disappear into pockets and drawers with no measurable engagement or sales insight.",
    icon: <VisibilityIcon sx={{ fontSize: 28, color: "#7CB5FF" }} />,
  },
];

const VALUE_PILLARS = [
  {
    title: "Brand Governance",
    body: "Centralise employee card templates, colours, logos, layouts and approved information across every office and division.",
    icon: <CorporateFareIcon sx={{ fontSize: 34, color: "#7CB5FF" }} />,
  },
  {
    title: "ESG & Sustainability",
    body: "Reduce paper, ink, courier logistics and physical waste while supporting paperless workplace initiatives and ESG reporting goals.",
    icon: <ForestIcon sx={{ fontSize: 34, color: "#70D7A5" }} />,
  },
  {
    title: "Secure Verification",
    body: "Help clients confirm that the person they are engaging is an active, company-approved representative with official details.",
    icon: <VerifiedIcon sx={{ fontSize: 34, color: "#7CB5FF" }} />,
  },
  {
    title: "Sales Enablement",
    body: "Turn every employee interaction into a measurable branded touchpoint with QR sharing, campaign links and engagement analytics.",
    icon: <SpeedIcon sx={{ fontSize: 34, color: "#7CB5FF" }} />,
  },
];

const HOW_IT_WORKS_STEPS = [
  "Configure company branding",
  "Add or import employees",
  "Generate QR-enabled digital cards",
  "Share by QR code, link, WhatsApp, or email",
  "Track engagement centrally",
];

const VERIFICATION_FEATURES = [
  "Verified company badge",
  "Verified employee badge",
  "One-click deactivation during offboarding",
  "Official company-controlled contact details",
];

const ESG_HIGHLIGHTS = [
  {
    metric: "Reduce paper consumption",
    body: "Replace high-volume print runs across branches, field teams and client-facing staff.",
  },
  {
    metric: "Eliminate chemical waste",
    body: "Remove ongoing use of inks, coatings and short-run print materials.",
  },
  {
    metric: "Minimise logistics emissions",
    body: "Cut courier movements and reprint distribution between head office and teams.",
  },
  {
    metric: "Enable sustainability reporting",
    body: "Support measurable digital transformation and ESG conversations with clients and boards.",
  },
];

const FEATURE_GRID = [
  {
    title: "Company-branded templates",
    icon: <GridViewIcon sx={{ fontSize: 26, color: "#7CB5FF" }} />,
  },
  {
    title: "QR code generation",
    icon: <QrCode2Icon sx={{ fontSize: 26, color: "#7CB5FF" }} />,
  },
  {
    title: "vCard downloads",
    icon: <BadgeIcon sx={{ fontSize: 26, color: "#7CB5FF" }} />,
  },
  {
    title: "Admin dashboard",
    icon: <DashboardIcon sx={{ fontSize: 26, color: "#7CB5FF" }} />,
  },
  {
    title: "Centralised management",
    icon: <WorkspacesIcon sx={{ fontSize: 26, color: "#7CB5FF" }} />,
  },
  {
    title: "Multi-company support",
    icon: <GroupsIcon sx={{ fontSize: 26, color: "#7CB5FF" }} />,
  },
  {
    title: "Mobile-first pages",
    icon: <PhoneIphoneIcon sx={{ fontSize: 26, color: "#7CB5FF" }} />,
  },
  {
    title: "Analytics-ready engagement",
    icon: <AnalyticsIcon sx={{ fontSize: 26, color: "#7CB5FF" }} />,
  },
];

const ANALYTICS_STATS = [
  { label: "Total Views", value: "12,480", delta: "+18%" },
  { label: "QR Scans", value: "4,210", delta: "+12%" },
  { label: "vCard Downloads", value: "1,365", delta: "+21%" },
];

const CHART_BARS = [
  { label: "Mon", value: 48 },
  { label: "Tue", value: 72 },
  { label: "Wed", value: 60 },
  { label: "Thu", value: 92 },
  { label: "Fri", value: 80 },
  { label: "Sat", value: 36 },
  { label: "Sun", value: 54 },
];

const INDUSTRY_USE_CASES = [
  {
    title: "Law & Accounting",
    body: "Standardise partner and associate corporate identity while keeping design, credentials and contact details under firm control.",
  },
  {
    title: "Banks & Microlenders",
    body: "Verify field agents and relationship managers to reduce impersonation risk during customer onboarding and collections.",
  },
  {
    title: "Real Estate",
    body: "Present agents professionally while linking directly to property listings, WhatsApp follow-up and verified profiles.",
  },
  {
    title: "Sales Teams",
    body: "Give every rep a frictionless way to share contact details, collect interest and turn meetings into measurable activity.",
  },
];

const COMPARISON_ROWS = [
  { label: "Reprinting required", printed: "Constant", digital: "No" },
  { label: "Brand control", printed: "Limited", digital: "Centralised" },
  { label: "Employee offboarding", printed: "Uncontrolled", digital: "Immediate" },
  { label: "Analytics", printed: "None", digital: "Built-in" },
  { label: "ESG impact", printed: "Waste-heavy", digital: "Paperless" },
  { label: "Contact saving", printed: "Manual typing", digital: "vCard ready" },
];

const PRICING_POINTS = [
  "Company setup fee",
  "Per-card or per-user plans",
  "Managed onboarding",
  "Enterprise controls",
];

const FOOTER_LINKS = [
  { label: "Features", href: "#features" },
  { label: "Verification", href: "#verification" },
  { label: "Analytics", href: "#analytics" },
  { label: "Pricing", href: "#pricing" },
];

const sectionCardSx = {
  height: "100%",
  borderRadius: 3,
  border: "1px solid rgba(255,255,255,0.08)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.015) 100%)",
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 28px 70px rgba(0,0,0,0.36)",
    borderColor: "rgba(255,255,255,0.2)",
  },
};

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [heroImageFailed, setHeroImageFailed] = useState(false);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#070707",
        color: "#F5F7FA",
        overflowX: "hidden",
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(7,7,7,0.78)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 72, md: 78 } }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "#FFFFFF",
            }}
          >
            BusinessCards BW
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction="row"
            spacing={1}
            sx={{ display: { xs: "none", md: "flex" }, mr: 2 }}
          >
            {NAV_LINKS.map((link) => (
              <Button
                key={link.label}
                component="a"
                href={link.href}
                sx={{
                  color: "#C7CDD6",
                  textTransform: "none",
                  px: 1.5,
                  "&:hover": {
                    color: "#FFFFFF",
                    bgcolor: "rgba(255,255,255,0.04)",
                  },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>

          <Button
            component="a"
            href={DEMO_EMAIL}
            variant="contained"
            sx={{
              display: { xs: "none", md: "inline-flex" },
              bgcolor: "#1F6BFF",
              color: "#FFFFFF",
              textTransform: "none",
              px: 2.5,
              py: 1.1,
              borderRadius: 2,
              boxShadow: "0 14px 36px rgba(31,107,255,0.28)",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "#3A7CFF",
                transform: "translateY(-2px)",
                boxShadow: "0 20px 40px rgba(31,107,255,0.34)",
              },
            }}
          >
            Request Demo
          </Button>

          <IconButton
            onClick={() => setMobileMenuOpen(true)}
            sx={{
              display: { xs: "inline-flex", md: "none" },
              color: "#FFFFFF",
              ml: 1,
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: "#101114",
            color: "#FFFFFF",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
            BusinessCards BW
          </Typography>
          <List sx={{ p: 0 }}>
            {NAV_LINKS.map((link) => (
              <ListItem key={link.label} disablePadding>
                <ListItemButton
                  component="a"
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  <ListItemText primary={link.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Button
            component="a"
            href={DEMO_EMAIL}
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              bgcolor: "#1F6BFF",
              color: "#FFFFFF",
              textTransform: "none",
              borderRadius: 2,
              py: 1.25,
              "&:hover": {
                bgcolor: "#3A7CFF",
              },
            }}
          >
            Request Demo
          </Button>
        </Box>
      </Drawer>

      <Box
        component="section"
        sx={{
          position: "relative",
          py: { xs: 10, md: 14 },
          background:
            "radial-gradient(circle at top left, rgba(31,107,255,0.16), transparent 34%), radial-gradient(circle at bottom right, rgba(112,215,165,0.12), transparent 28%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="Secure digital identity for modern teams"
                sx={{
                  mb: 3,
                  color: "#D6E5FF",
                  bgcolor: "rgba(31,107,255,0.12)",
                  border: "1px solid rgba(31,107,255,0.3)",
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.6rem", sm: "3.2rem", md: "4.3rem" },
                  lineHeight: 1.04,
                  letterSpacing: "-0.04em",
                  fontWeight: 800,
                  color: "#FFFFFF",
                  maxWidth: 720,
                }}
              >
                Secure Digital Business Cards for Modern Organisations
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mt: 3,
                  color: "#DDE3EA",
                  fontWeight: 500,
                  fontSize: { xs: "1.2rem", md: "1.45rem" },
                }}
              >
                Control your brand. Verify your people. Reduce printing waste.
              </Typography>
              <Typography
                sx={{
                  mt: 3,
                  color: "#A0A7B4",
                  fontSize: { xs: "1rem", md: "1.08rem" },
                  lineHeight: 1.8,
                  maxWidth: 620,
                }}
              >
                BusinessCards BW gives companies centrally managed, QR-enabled
                digital business cards with brand control, employee
                verification, vCard downloads, and analytics-ready engagement
                tracking.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 4 }}
              >
                <Button
                  component="a"
                  href={DEMO_EMAIL}
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: "#1F6BFF",
                    color: "#FFFFFF",
                    textTransform: "none",
                    px: 3.5,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: "1rem",
                    boxShadow: "0 18px 40px rgba(31,107,255,0.28)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "#3A7CFF",
                      transform: "translateY(-4px)",
                      boxShadow: "0 24px 48px rgba(31,107,255,0.34)",
                    },
                  }}
                >
                  Request a Demo
                </Button>
                <Button
                  component="a"
                  href="#verification"
                  variant="outlined"
                  size="large"
                  sx={{
                    color: "#FFFFFF",
                    borderColor: "rgba(255,255,255,0.22)",
                    textTransform: "none",
                    px: 3.5,
                    py: 1.5,
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.4)",
                      bgcolor: "rgba(255,255,255,0.04)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  See a Live Card
                </Button>
              </Stack>

              <Stack
                direction="row"
                spacing={1.2}
                useFlexGap
                flexWrap="wrap"
                sx={{ mt: 4 }}
              >
                {TRUST_BADGES.map((badge) => (
                  <Chip
                    key={badge}
                    label={badge}
                    icon={<CheckCircleIcon sx={{ color: "#70D7A5 !important" }} />}
                    sx={{
                      color: "#D2D8E2",
                      bgcolor: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  />
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  minHeight: { xs: 360, md: 540 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!heroImageFailed ? (
                  <Box
                    component="img"
                    src="/assets/business-cards.png"
                    alt="BusinessCards BW digital business cards"
                    onError={() => setHeroImageFailed(true)}
                    sx={{
                      width: "100%",
                      maxWidth: 620,
                      borderRadius: 4,
                      border: "1px solid rgba(255,255,255,0.08)",
                      boxShadow: "0 34px 80px rgba(0,0,0,0.42)",
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      maxWidth: 540,
                      height: { xs: 340, md: 470 },
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 30,
                        left: { xs: 24, md: 60 },
                        width: { xs: 250, md: 320 },
                        height: { xs: 170, md: 210 },
                        borderRadius: 3,
                        border: "1px solid rgba(255,255,255,0.08)",
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                        backdropFilter: "blur(16px)",
                        transform: "rotate(-8deg)",
                        boxShadow: "0 25px 60px rgba(0,0,0,0.34)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: 90,
                        right: { xs: 16, md: 40 },
                        width: { xs: 260, md: 330 },
                        height: { xs: 190, md: 225 },
                        borderRadius: 3,
                        border: "1px solid rgba(255,255,255,0.1)",
                        background:
                          "linear-gradient(180deg, rgba(31,107,255,0.18), rgba(255,255,255,0.03))",
                        backdropFilter: "blur(18px)",
                        transform: "rotate(6deg)",
                        boxShadow: "0 28px 70px rgba(0,0,0,0.38)",
                      }}
                    />
                    <Card
                      sx={{
                        ...sectionCardSx,
                        position: "absolute",
                        left: { xs: 24, md: 92 },
                        top: { xs: 70, md: 110 },
                        width: { xs: 270, md: 360 },
                        p: 1,
                        background:
                          "linear-gradient(160deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
                        backdropFilter: "blur(20px)",
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{
                                width: 54,
                                height: 54,
                                bgcolor: "#1F6BFF",
                                fontWeight: 700,
                              }}
                            >
                              KB
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700 }}>
                                Kabelo Bantsi
                              </Typography>
                              <Typography sx={{ color: "#A0A7B4", fontSize: 14 }}>
                                Corporate Affairs Manager
                              </Typography>
                            </Box>
                          </Stack>
                          <VerifiedIcon sx={{ color: "#70D7A5", fontSize: 28 }} />
                        </Stack>

                        <Divider
                          sx={{ my: 2.5, borderColor: "rgba(255,255,255,0.08)" }}
                        />

                        <Grid container spacing={1.5}>
                          <Grid item xs={7}>
                            <Stack spacing={1.2}>
                              <Chip
                                label="Verified Employee"
                                icon={<BadgeIcon />}
                                sx={{
                                  justifyContent: "flex-start",
                                  bgcolor: "rgba(255,255,255,0.05)",
                                  color: "#F5F7FA",
                                  border: "1px solid rgba(255,255,255,0.08)",
                                }}
                              />
                              <Chip
                                label="Company Controlled"
                                icon={<CorporateFareIcon />}
                                sx={{
                                  justifyContent: "flex-start",
                                  bgcolor: "rgba(31,107,255,0.12)",
                                  color: "#DDE7FF",
                                  border: "1px solid rgba(31,107,255,0.22)",
                                }}
                              />
                            </Stack>
                          </Grid>
                          <Grid item xs={5}>
                            <Box
                              sx={{
                                height: "100%",
                                borderRadius: 2,
                                border: "1px solid rgba(255,255,255,0.08)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                              }}
                            >
                              <QrCode2Icon sx={{ color: "#FFFFFF", fontSize: 76 }} />
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, bgcolor: "#0D0D0D" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 800,
              color: "#FFFFFF",
              textAlign: "center",
              mb: 2,
            }}
          >
            Printed Business Cards Create More Risk Than You Think
          </Typography>
          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              textAlign: "center",
              color: "#A0A7B4",
              lineHeight: 1.8,
              mb: 6,
            }}
          >
            For regulated teams, distributed salesforces and client-facing
            organisations, printed cards are harder to govern than they appear.
          </Typography>
          <Grid container spacing={3}>
            {PROBLEM_ITEMS.map((item) => (
              <Grid item xs={12} sm={6} lg={3} key={item.title}>
                <Card sx={sectionCardSx}>
                  <CardContent sx={{ p: 3.25 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        mb: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(31,107,255,0.1)",
                        border: "1px solid rgba(31,107,255,0.22)",
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 1.5 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: "#A0A7B4", lineHeight: 1.8 }}>
                      {item.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, bgcolor: "#111214" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 800,
              textAlign: "center",
              mb: 2,
            }}
          >
            One Platform. Four Business Outcomes.
          </Typography>
          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              textAlign: "center",
              color: "#A0A7B4",
              lineHeight: 1.8,
              mb: 6,
            }}
          >
            Designed for organisations that need stronger identity control,
            cleaner ESG execution and better field-level trust.
          </Typography>
          <Grid container spacing={3}>
            {VALUE_PILLARS.map((pillar) => (
              <Grid item xs={12} md={6} key={pillar.title}>
                <Card sx={{ ...sectionCardSx, minHeight: 250 }}>
                  <CardContent sx={{ p: 3.5 }}>
                    <Box
                      sx={{
                        width: 62,
                        height: 62,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor:
                          pillar.title === "ESG & Sustainability"
                            ? "rgba(112,215,165,0.1)"
                            : "rgba(31,107,255,0.1)",
                        border:
                          pillar.title === "ESG & Sustainability"
                            ? "1px solid rgba(112,215,165,0.26)"
                            : "1px solid rgba(31,107,255,0.22)",
                        mb: 3,
                      }}
                    >
                      {pillar.icon}
                    </Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1.5 }}>
                      {pillar.title}
                    </Typography>
                    <Typography sx={{ color: "#A0A7B4", lineHeight: 1.85 }}>
                      {pillar.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, bgcolor: "#0D0D0D" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 800,
              textAlign: "center",
              mb: 2,
            }}
          >
            From Setup to Sharing in Minutes
          </Typography>
          <Typography
            sx={{
              maxWidth: 720,
              mx: "auto",
              textAlign: "center",
              color: "#A0A7B4",
              lineHeight: 1.8,
              mb: 6,
            }}
          >
            Launch centrally managed digital business cards across Botswana and
            regional teams without rebuilding your workflow.
          </Typography>
          <Grid container spacing={3}>
            {HOW_IT_WORKS_STEPS.map((step, index) => (
              <Grid item xs={12} md={index === HOW_IT_WORKS_STEPS.length - 1 ? 12 : 6} key={step}>
                <Card sx={sectionCardSx}>
                  <CardContent sx={{ p: 3.25 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Box
                        sx={{
                          minWidth: 42,
                          height: 42,
                          borderRadius: "50%",
                          bgcolor: "rgba(31,107,255,0.14)",
                          border: "1px solid rgba(31,107,255,0.26)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#DDE7FF",
                          fontWeight: 800,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography sx={{ fontSize: 20, fontWeight: 600, pt: 0.6 }}>
                        {step}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        id="verification"
        component="section"
        sx={{ py: { xs: 8, md: 11 }, bgcolor: "#111214" }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.7rem" },
                  fontWeight: 800,
                  mb: 2,
                }}
              >
                Reduce Impersonation Risk with Verified Digital Profiles
              </Typography>
              <Typography
                sx={{
                  color: "#A0A7B4",
                  lineHeight: 1.85,
                  mb: 3,
                  fontSize: "1.02rem",
                }}
              >
                Printed cards continue circulating after employees leave.
                Digital profiles can be updated, deactivated, or redirected
                centrally in real-time.
              </Typography>
              <Stack spacing={2}>
                {VERIFICATION_FEATURES.map((feature) => (
                  <Stack direction="row" spacing={1.5} alignItems="center" key={feature}>
                    <CheckCircleIcon sx={{ color: "#70D7A5" }} />
                    <Typography sx={{ color: "#D7DCE5" }}>{feature}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ ...sectionCardSx, p: { xs: 1, md: 2 } }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                  >
                    <Stack direction="row" spacing={2}>
                      <Avatar
                        sx={{
                          width: 66,
                          height: 66,
                          bgcolor: "#1F6BFF",
                          fontWeight: 800,
                        }}
                      >
                        NM
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 22 }}>
                          Naledi Mookodi
                        </Typography>
                        <Typography sx={{ color: "#A0A7B4", mb: 1 }}>
                          Relationship Manager
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip
                            label="Verified Employee"
                            icon={<FactCheckIcon />}
                            sx={{
                              bgcolor: "rgba(31,107,255,0.12)",
                              color: "#DDE7FF",
                              border: "1px solid rgba(31,107,255,0.22)",
                            }}
                          />
                          <Chip
                            label="Active"
                            icon={<CheckCircleIcon />}
                            sx={{
                              bgcolor: "rgba(112,215,165,0.1)",
                              color: "#D8F5E6",
                              border: "1px solid rgba(112,215,165,0.22)",
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>

                    <Box
                      sx={{
                        width: 92,
                        height: 92,
                        borderRadius: 2.5,
                        border: "1px solid rgba(255,255,255,0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
                      }}
                    >
                      <QrCode2Icon sx={{ fontSize: 62, color: "#FFFFFF" }} />
                    </Box>
                  </Stack>

                  <Divider
                    sx={{ my: 3, borderColor: "rgba(255,255,255,0.08)" }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          borderRadius: 2.5,
                          border: "1px solid rgba(255,255,255,0.08)",
                          bgcolor: "rgba(255,255,255,0.02)",
                        }}
                      >
                        <CardContent sx={{ p: 2.25 }}>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <CorporateFareIcon sx={{ color: "#7CB5FF" }} />
                            <Box>
                              <Typography sx={{ fontWeight: 700 }}>
                                Company-controlled
                              </Typography>
                              <Typography sx={{ color: "#A0A7B4", fontSize: 14 }}>
                                Branding and details are managed centrally.
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          borderRadius: 2.5,
                          border: "1px solid rgba(255,255,255,0.08)",
                          bgcolor: "rgba(255,255,255,0.02)",
                        }}
                      >
                        <CardContent sx={{ p: 2.25 }}>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <VpnKeyIcon sx={{ color: "#70D7A5" }} />
                            <Box>
                              <Typography sx={{ fontWeight: 700 }}>
                                Instant offboarding
                              </Typography>
                              <Typography sx={{ color: "#A0A7B4", fontSize: 14 }}>
                                Disable access without chasing physical stock.
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        id="esg"
        component="section"
        sx={{
          py: { xs: 8, md: 11 },
          bgcolor: "#0D0D0D",
          background:
            "radial-gradient(circle at top right, rgba(112,215,165,0.1), transparent 26%)",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 800,
              textAlign: "center",
              mb: 2,
            }}
          >
            A Greener Way to Network
          </Typography>
          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              textAlign: "center",
              color: "#A0A7B4",
              lineHeight: 1.8,
              mb: 6,
            }}
          >
            Replace disposable printed cards with a paperless networking
            solution that supports ESG reporting and digital transformation
            goals.
          </Typography>
          <Grid container spacing={3}>
            {ESG_HIGHLIGHTS.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.metric}>
                <Card sx={sectionCardSx}>
                  <CardContent sx={{ p: 3 }}>
                    <ForestIcon sx={{ color: "#70D7A5", fontSize: 30, mb: 2 }} />
                    <Typography
                      sx={{
                        color: "#70D7A5",
                        fontWeight: 700,
                        fontSize: 19,
                        mb: 1.4,
                      }}
                    >
                      {item.metric}
                    </Typography>
                    <Typography sx={{ color: "#A0A7B4", lineHeight: 1.75 }}>
                      {item.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        id="features"
        component="section"
        sx={{ py: { xs: 8, md: 11 }, bgcolor: "#111214" }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 800,
              textAlign: "center",
              mb: 2,
            }}
          >
            Everything Your Team Needs to Share Professionally
          </Typography>
          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              textAlign: "center",
              color: "#A0A7B4",
              lineHeight: 1.8,
              mb: 6,
            }}
          >
            Built for organisations that want stronger corporate presentation
            across employees, branches and external partners.
          </Typography>
          <Grid container spacing={2.5}>
            {FEATURE_GRID.map((feature) => (
              <Grid item xs={12} sm={6} md={3} key={feature.title}>
                <Card sx={sectionCardSx}>
                  <CardContent sx={{ p: 2.75 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(31,107,255,0.1)",
                        border: "1px solid rgba(31,107,255,0.2)",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                      {feature.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        id="analytics"
        component="section"
        sx={{ py: { xs: 8, md: 11 }, bgcolor: "#0D0D0D" }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.7rem" },
                  fontWeight: 800,
                  mb: 2,
                }}
              >
                Turn Handshakes into Data
              </Typography>
              <Typography
                sx={{
                  color: "#A0A7B4",
                  lineHeight: 1.85,
                  mb: 3,
                  fontSize: "1.02rem",
                }}
              >
                Printed cards disappear into desk drawers. Digital cards create
                measurable engagement, helping marketing, sales and operations
                teams understand what happens after the introduction.
              </Typography>
              <Stack spacing={2}>
                {ANALYTICS_STATS.map((stat) => (
                  <Card key={stat.label} sx={sectionCardSx}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography sx={{ color: "#A0A7B4", fontSize: 14 }}>
                            {stat.label}
                          </Typography>
                          <Typography sx={{ fontSize: 28, fontWeight: 800 }}>
                            {stat.value}
                          </Typography>
                        </Box>
                        <Chip
                          label={stat.delta}
                          sx={{
                            color: "#70D7A5",
                            bgcolor: "rgba(112,215,165,0.1)",
                            border: "1px solid rgba(112,215,165,0.2)",
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Card sx={{ ...sectionCardSx, minHeight: 420 }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 4 }}
                  >
                    <Box>
                      <Typography sx={{ color: "#A0A7B4", fontSize: 14 }}>
                        Weekly engagement snapshot
                      </Typography>
                      <Typography sx={{ fontSize: 24, fontWeight: 800 }}>
                        Card interaction trend
                      </Typography>
                    </Box>
                    <BarChartIcon sx={{ color: "#7CB5FF", fontSize: 32 }} />
                  </Stack>

                  <Box
                    sx={{
                      height: 260,
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      gap: 1.2,
                    }}
                  >
                    {CHART_BARS.map((bar) => (
                      <Stack
                        key={bar.label}
                        spacing={1.2}
                        alignItems="center"
                        sx={{ flex: 1, height: "100%" }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            flexGrow: 1,
                            display: "flex",
                            alignItems: "flex-end",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              height: `${bar.value}%`,
                              minHeight: 36,
                              borderRadius: 2,
                              background:
                                bar.value > 75
                                  ? "linear-gradient(180deg, #70D7A5 0%, #2BB673 100%)"
                                  : "linear-gradient(180deg, #69A4FF 0%, #1F6BFF 100%)",
                              boxShadow:
                                bar.value > 75
                                  ? "0 12px 30px rgba(43,182,115,0.25)"
                                  : "0 12px 30px rgba(31,107,255,0.25)",
                            }}
                          />
                        </Box>
                        <Typography sx={{ color: "#A0A7B4", fontSize: 13 }}>
                          {bar.label}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, bgcolor: "#111214" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 800,
              textAlign: "center",
              mb: 2,
            }}
          >
            Built for Teams Where Trust and Presentation Matter
          </Typography>
          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              textAlign: "center",
              color: "#A0A7B4",
              lineHeight: 1.8,
              mb: 6,
            }}
          >
            Ideal for sectors across Botswana and the region where professional
            identity, verification and consistent branding directly affect
            revenue and trust.
          </Typography>
          <Grid container spacing={3}>
            {INDUSTRY_USE_CASES.map((industry) => (
              <Grid item xs={12} sm={6} key={industry.title}>
                <Card sx={sectionCardSx}>
                  <CardContent sx={{ p: 3.25 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 21, mb: 1.3 }}>
                      {industry.title}
                    </Typography>
                    <Typography sx={{ color: "#A0A7B4", lineHeight: 1.8 }}>
                      {industry.body}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 11 }, bgcolor: "#0D0D0D" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.7rem" },
              fontWeight: 800,
              textAlign: "center",
              mb: 2,
            }}
          >
            Stop Paying for Cards That Become Outdated
          </Typography>
          <Typography
            sx={{
              maxWidth: 760,
              mx: "auto",
              textAlign: "center",
              color: "#A0A7B4",
              lineHeight: 1.8,
              mb: 6,
            }}
          >
            Compare the operational reality of printed cards with a centrally
            managed digital identity platform.
          </Typography>
          <TableContainer
            sx={{
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.08)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015))",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.08)", fontWeight: 700 }}>
                    Capability
                  </TableCell>
                  <TableCell sx={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.08)", fontWeight: 700 }}>
                    Printed Cards
                  </TableCell>
                  <TableCell sx={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.08)", fontWeight: 700 }}>
                    BusinessCards BW
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {COMPARISON_ROWS.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell sx={{ color: "#E9EDF3", borderColor: "rgba(255,255,255,0.08)" }}>
                      {row.label}
                    </TableCell>
                    <TableCell sx={{ color: "#A0A7B4", borderColor: "rgba(255,255,255,0.08)" }}>
                      {row.printed}
                    </TableCell>
                    <TableCell sx={{ color: "#E9EDF3", borderColor: "rgba(255,255,255,0.08)" }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon sx={{ color: "#70D7A5", fontSize: 18 }} />
                        <Typography component="span">{row.digital}</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      <Box
        id="pricing"
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: "#111214",
          background:
            "radial-gradient(circle at bottom left, rgba(31,107,255,0.12), transparent 28%)",
        }}
      >
        <Container maxWidth="md">
          <Card
            sx={{
              ...sectionCardSx,
              textAlign: "center",
              background:
                "linear-gradient(180deg, rgba(31,107,255,0.08), rgba(255,255,255,0.02))",
            }}
          >
            <CardContent sx={{ p: { xs: 3.5, md: 5 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2rem", md: "2.8rem" },
                  fontWeight: 800,
                  mb: 2,
                }}
              >
                Ready to Replace Printed Business Cards?
              </Typography>
              <Typography
                sx={{
                  color: "#A0A7B4",
                  lineHeight: 1.85,
                  maxWidth: 720,
                  mx: "auto",
                  mb: 4,
                }}
              >
                Start with a branded company setup, onboard your team, and give
                every employee a professional, secure, shareable digital
                identity.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 4 }}>
                {PRICING_POINTS.map((point) => (
                  <Grid item xs={12} sm={6} key={point}>
                    <Card
                      sx={{
                        borderRadius: 2.5,
                        border: "1px solid rgba(255,255,255,0.08)",
                        bgcolor: "rgba(255,255,255,0.02)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          borderColor: "rgba(255,255,255,0.2)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.25 }}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <CheckCircleIcon sx={{ color: "#70D7A5" }} />
                          <Typography sx={{ color: "#E9EDF3", fontWeight: 600 }}>
                            {point}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Button
                component="a"
                href={DEMO_EMAIL}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: "#1F6BFF",
                  color: "#FFFFFF",
                  textTransform: "none",
                  px: 4,
                  py: 1.6,
                  borderRadius: 2,
                  fontSize: "1rem",
                  boxShadow: "0 18px 40px rgba(31,107,255,0.28)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "#3A7CFF",
                    transform: "translateY(-4px)",
                    boxShadow: "0 24px 48px rgba(31,107,255,0.34)",
                  },
                }}
              >
                Request a Demo
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 5,
          bgcolor: "#070707",
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Typography sx={{ fontSize: 22, fontWeight: 800, mb: 1.5 }}>
                BusinessCards BW
              </Typography>
              <Typography sx={{ color: "#A0A7B4", lineHeight: 1.8, maxWidth: 420 }}>
                Secure, centrally managed digital identity for corporate teams
                that care about brand governance, verification, ESG and
                measurable engagement.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Explore</Typography>
              <Stack spacing={1}>
                {FOOTER_LINKS.map((link) => (
                  <Typography
                    key={link.label}
                    component="a"
                    href={link.href}
                    sx={{
                      color: "#A0A7B4",
                      textDecoration: "none",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        color: "#FFFFFF",
                      },
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Platform Focus</Typography>
              <Stack spacing={1}>
                <Typography sx={{ color: "#A0A7B4" }}>
                  Corporate identity management
                </Typography>
                <Typography sx={{ color: "#A0A7B4" }}>
                  Employee verification
                </Typography>
                <Typography sx={{ color: "#A0A7B4" }}>
                  QR and vCard sharing
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.08)" }} />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            justifyContent="space-between"
          >
            <Typography sx={{ color: "#7F8794" }}>
              © {new Date().getFullYear()} BusinessCards BW. All rights reserved.
            </Typography>
            <Typography sx={{ color: "#7F8794" }}>
              Built for modern organisations across Botswana and Africa.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
